/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { localZone } from '@/lib/clock';

/** The months as every date in Vellum writes them: short, and in English. */
export const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * A date as a person writes it: `4 Sep 2026`.
 *
 * Parsed from the ISO string rather than through `Date`, because `new
 * Date('2026-09-04')` is midnight UTC and renders as the third of September to
 * anybody west of Greenwich. A contract date must not shift by timezone.
 */
export function formatDate(iso: string | null | undefined, fallback = 'Not set'): string {
  if (!iso) return fallback;
  const [year, month, day] = iso.slice(0, 10).split('-');
  const index = Number(month) - 1;
  if (!year || !day || Number.isNaN(index) || index < 0 || index > 11) return fallback;
  return `${String(Number(day))} ${MONTHS[index] ?? ''} ${year}`;
}

const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];
const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * A day written out in full, as a page heading says it:
 * `Wednesday, 30 September 2026`. From the ISO day, so it cannot shift by zone.
 */
export function longDate(iso: string): string {
  const [year, month, day] = iso.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return '';
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return `${WEEKDAYS[weekday] ?? ''}, ${String(day)} ${MONTHS_LONG[month - 1] ?? ''} ${String(year)}`;
}

/**
 * A moment split in two for a table cell, on the reader's own clock:
 * `{ day: '29 Sep 2026', time: '09:40' }`.
 *
 * A contract date is a day and must not move; a moment is an instant, and
 * should. Built from its parts so it reads the same in every browser.
 */
export function dayAndTime(
  iso: string | null | undefined,
  zone: string = localZone(),
): { day: string; time: string } | null {
  if (!iso) return null;
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return null;
  // Numeric month, named from MONTHS: some locales write September "Sept".
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(when);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((found) => found.type === type)?.value ?? '';
  return {
    day: `${part('day')} ${MONTHS[Number(part('month')) - 1] ?? ''} ${part('year')}`,
    time: `${part('hour')}:${part('minute')}`,
  };
}

/** A moment on one line: `29 Sep 2026, 09:40`. */
export function dayTimeLine(
  iso: string | null | undefined,
  fallback = '',
  zone: string = localZone(),
): string {
  const moment = dayAndTime(iso, zone);
  return moment ? `${moment.day}, ${moment.time}` : fallback;
}

/** A moment with its time, as every screen writes it: `5 Oct 2026, 08:00`. */
export function formatDateTime(
  iso: string | null | undefined,
  fallback = 'Not set',
  zone: string = localZone(),
): string {
  return dayTimeLine(iso, fallback, zone);
}

/**
 * A recent moment, short, on the reader's clock: `2 Oct, 14:05`. For things
 * that happened this week -- a reminder sent -- where the year is noise.
 */
export function shortMoment(
  iso: string | null | undefined,
  fallback = '',
  zone: string = localZone(),
): string {
  const moment = dayAndTime(iso, zone);
  if (!moment) return fallback;
  const [day, month] = moment.day.split(' ');
  return `${day ?? ''} ${month ?? ''}, ${moment.time}`;
}
